import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { CheckCircle2, Building2, Users, FileText, ChevronRight } from 'lucide-react';

interface MarketingOnboardingProps {
  onComplete: () => void;
}

const MarketingOnboarding: React.FC<MarketingOnboardingProps> = ({ onComplete }) => {
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome to SignatureGuestbook!",
      icon: <CheckCircle2 className="w-12 h-12 text-primary" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            We're thrilled to have you here! SignatureGuestbook helps funeral homes create meaningful digital guestbooks for families.
          </p>
          <p className="text-gray-600">
            Let's get your account set up so you can start serving families with our digital memorial solutions.
          </p>
        </div>
      )
    },
    {
      title: "Tell us about your funeral home",
      icon: <Building2 className="w-12 h-12 text-primary" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            We'd love to learn more about your funeral home to better serve your needs.
          </p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <span>How many services do you handle monthly?</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <span>What technology do you currently use?</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <span>How did you hear about us?</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      title: "Key Features",
      icon: <FileText className="w-12 h-12 text-primary" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">Here's what you can do with SignatureGuestbook:</p>
          <div className="grid gap-3">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-1" />
              <div>
                <strong>Digital Guestbooks</strong>
                <p className="text-sm text-gray-600">Collect signatures on iPads or via QR codes</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-1" />
              <div>
                <strong>Automated Follow-ups</strong>
                <p className="text-sm text-gray-600">Send personalized emails to attendees</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-1" />
              <div>
                <strong>Beautiful Keepsakes</strong>
                <p className="text-sm text-gray-600">Create printed books and digital memories</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Your team",
      icon: <Users className="w-12 h-12 text-primary" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            You can invite your team members to collaborate on SignatureGuestbook.
          </p>
          <p className="text-gray-600">
            Each team member can have their own login and manage services together.
          </p>
          <div className="bg-primary-light rounded-lg p-4">
            <p className="text-sm">
              <strong>Pro tip:</strong> You can add team members from the Account Settings page after setup.
            </p>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as complete
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {/* Progress indicator */}
          <div className="flex justify-center mb-6">
            <div className="flex gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-12 rounded-full transition-colors ${
                    index <= currentStep ? 'bg-primary' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              {step.icon}
            </div>
            <h2 className="text-2xl font-bold mb-4">{step.title}</h2>
            <div className="text-left">
              {step.content}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-6 border-t">
            <button
              onClick={handleSkip}
              className="btn btn-link text-gray-600"
            >
              Skip tour
            </button>
            <div className="flex gap-3">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="btn btn-light"
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="btn btn-primary flex items-center gap-2"
              >
                {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingOnboarding;